import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { useAxiosAuth0 } from "@shared/config/useAxiosAuth0";
import { fetchAuditLogsFilterOptions } from "../requests/request";
import type { AuditLogsFilterOptions } from "../types";

interface UseAuditLogsFilterOptionsResult {
  data: AuditLogsFilterOptions | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

interface UseAuditLogsFilterOptionsOptions {
  /**
   * Tenant scope to forward to `/filters/options`. Caches are keyed by
   * `(tenantId, productId)` so that a partner switching to a customer
   * (and back) doesn't see stale options. See
   * `AuditLogsQueryParams.tenantId` for semantics.
   */
  tenantId?: number | null;
  /**
   * Optional product scope. When set, the backend narrows the
   * `resourceTypes` array to the types belonging to that product so the
   * "Resource type" dropdown only shows types that actually apply (e.g.
   * picking Kontrol Edge hides Reflector resource types).
   */
  productId?: number | null;
}

/**
 * Loads `/api/v2/audit-logs/filters/options` once per ~10 minutes per
 * `(tenant, product)` pair (cached at module scope inside `requests/request.ts`).
 *
 * Surface contract:
 *   - `isLoading` flips on for the very first fetch of each
 *     `(tenant, product)` pair only — subsequent mounts hit the cache
 *     synchronously.
 *   - `error` carries the backend `message` when available.
 *   - `data.products.length === 0` is a valid state and means the tenant
 *     has no audit-enabled products. The page is responsible for showing
 *     the "no access" empty state in that case.
 */
export function useAuditLogsFilterOptions(
  options: UseAuditLogsFilterOptionsOptions = {}
): UseAuditLogsFilterOptionsResult {
  const api = useAxiosAuth0();
  const tenantId = options.tenantId ?? null;
  const productId = options.productId ?? null;
  const [data, setData] = useState<AuditLogsFilterOptions | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const isMountedRef = useRef<boolean>(true);
  const [refreshNonce, setRefreshNonce] = useState<number>(0);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError(null);

    fetchAuditLogsFilterOptions(api, {
      forceRefresh: refreshNonce > 0,
      tenantId,
      productId,
    })
      .then((result) => {
        if (cancelled || !isMountedRef.current) return;
        setData(result);
      })
      .catch((err: unknown) => {
        if (cancelled || !isMountedRef.current) return;
        setError(extractErrorMessage(err, "Failed to load filter options"));
        setData(null);
      })
      .finally(() => {
        if (cancelled || !isMountedRef.current) return;
        setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [api, refreshNonce, tenantId, productId]);

  const refetch = useCallback(() => {
    setRefreshNonce((n) => n + 1);
  }, []);

  return { data, isLoading, error, refetch };
}

function extractErrorMessage(err: unknown, fallback: string): string {
  if (axios.isAxiosError(err)) {
    const body = err.response?.data as { message?: string } | undefined;
    if (body?.message) return body.message;
    if (err.message) return err.message;
  }
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}
